import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';

import { environment, environment as env } from '../environments/environment';

import { provideAngularSvgIcon } from 'angular-svg-icon';
import {
  RollbarErrorHandler,
  rollbarFactory,
  RollbarService,
} from './util/errors/error-handler.service';
import { ExtendedAuthService } from './util/auth/auth.service';
import { provideIdentityManagers } from './util/identity/identity-managers';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { DATE_FNS_LOCALE } from './util/date';
import { routes } from './app.routes';
import { provideMarkdown } from 'ngx-markdown';
import { provideMatPaginatorIntl } from './util/i18n/mat-paginator';
import { GoogleTagManagerModule } from './util/tracking/google-analytics/google-tag-manager.module';
import { ghostingInterceptorFn } from './util/auth/ghosting/ghosting.interceptor';
import { CurrencyPipe } from '@angular/common';
import { provideVersionUpdates } from './util/pwa/version-update.service';
import { PageMetadataService } from './util/router/page-metadata.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([authHttpInterceptorFn, ghostingInterceptorFn]),
    ),
    provideMarkdown({ loader: HttpClient }),
    provideRouter(routes),
    provideAuth0({
      ...env.auth,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [
          {
            uri: new URL('*', env.backendUri).href,
            allowAnonymous: true,
          },
        ],
      },
    }),
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    provideAngularSvgIcon(),
    provideIdentityManagers(),
    ExtendedAuthService,
    provideDateFnsAdapter(),
    provideMatPaginatorIntl(),
    { provide: MAT_DATE_LOCALE, useValue: DATE_FNS_LOCALE },
    importProvidersFrom(
      GoogleTagManagerModule.forRoot(environment.googleTagManager),
    ),
    provideVersionUpdates(),
    CurrencyPipe,
    PageMetadataService,
    {
      // Start page metadata service when app starts
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [PageMetadataService],
      multi: true,
    },
  ],
};
